import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/*
  TutorCenterMapPanel - Uses hkLocations data for precise coordinates and bilingual names
  - 3 zoom levels:
    <=13 : region (3 markers: 新界, 香港島, 九龍) - merged from hkLocations
    14-15 : district by districtValue (one marker per district from hkLocations)
    >=16 : MapMarkerGroupID clusters (one per group) with list info

  Behavior:
  - Clicking a region zooms into that region and shows districts
  - Clicking a district zooms into that district and shows MapMarkerGroupID clusters
  - Clicking a cluster shows an InfoWindow listing centers in that cluster
*/

function TutorCenterMapPanel({
  hkLocations = {},
  centers = [],
  onSelectCenter,
}) {
  const { t, i18n } = useTranslation();
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const [map, setMap] = useState(null);

  // marker groups for each level
  const [regionMarkers, setRegionMarkers] = useState([]);
  const [districtMarkers, setDistrictMarkers] = useState([]);
  const [groupMarkers, setGroupMarkers] = useState([]);
  const [individualMarkers, setIndividualMarkers] = useState([]);

  // UI state
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [visibleCenters, setVisibleCenters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Build lookup maps from hkLocations
  const [districtLookup, setDistrictLookup] = useState({});
  const [areaToRegion, setAreaToRegion] = useState({});

  useEffect(() => {
    // Build district lookup: districtValue -> { lat, lng, display }
    const lookup = {};
    const areaMap = {};

    if (hkLocations && hkLocations.regions) {
      hkLocations.regions.forEach((region) => {
        const regionValue = region.value;
        if (region.areas) {
          region.areas.forEach((area) => {
            const areaValue = area.value;
            areaMap[areaValue] = regionValue;
            lookup[areaValue] = {
              lat: parseFloat(area.lat),
              lng: parseFloat(area.lng),
              display: area.display || {},
              regionValue: regionValue,
            };
          });
        }
      });
    }

    setDistrictLookup(lookup);
    setAreaToRegion(areaMap);
  }, [hkLocations]);

  // small helpers
  const makeSlug = (title) => {
    if (!title) return "";
    return title
      .toString()
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // load Google Maps script if needed
  useEffect(() => {
    if (!window.google) {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
      if (!apiKey) {
        console.warn("REACT_APP_GOOGLE_MAPS_API_KEY is not set");
      }
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait a bit for Google Maps to fully initialize
        setTimeout(() => setIsLoading(false), 100);
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps script");
        setIsLoading(false);
      };
      document.head.appendChild(script);
    } else {
      setIsLoading(false);
    }
  }, []);

  // init map
  useEffect(() => {
    if (
      !isLoading &&
      window.google &&
      window.google.maps &&
      mapRef.current &&
      !map
    ) {
      try {
        const hongKongCenter = { lat: 22.3193, lng: 114.1694 };
        const newMap = new window.google.maps.Map(mapRef.current, {
          center: hongKongCenter,
          zoom: 11,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        });
        setMap(newMap);
      } catch (error) {
        console.error("Failed to initialize map:", error);
      }
    }
  }, [isLoading, map]);

  // utility: safely clear markers array and call setMap(null)
  const clearMarkers = (markers) => {
    try {
      markers.forEach((m) => {
        if (m && typeof m.setMap === "function") m.setMap(null);
      });
    } catch (e) {
      // ignore
    }
  };

  // build aggregated structures from centers
  const computeRegionGroups = useCallback(() => {
    // Merge 5 regions from hkLocations into 3 display regions:
    // new_territories (new_territories_east + new_territories_west)
    // hong_kong_island
    // kowloon (kowloon_west + kowloon_east)
    const groups = { new_territories: [], hong_kong_island: [], kowloon: [] };

    centers.forEach((c) => {
      const districtVal = (c.districtValue || "").toString().trim();
      const regionVal = (c.regionValue || c.region || "").toString().trim();

      // First try to map via districtValue using areaToRegion lookup
      let mappedRegion = null;
      if (districtVal && areaToRegion[districtVal]) {
        const hkRegion = areaToRegion[districtVal];
        if (hkRegion.startsWith("new_territories")) {
          mappedRegion = "new_territories";
        } else if (hkRegion === "hong_kong_island") {
          mappedRegion = "hong_kong_island";
        } else if (hkRegion.startsWith("kowloon")) {
          mappedRegion = "kowloon";
        }
      }

      // Fallback to regionValue
      if (!mappedRegion) {
        if (regionVal.startsWith("new_territories")) {
          mappedRegion = "new_territories";
        } else if (regionVal.startsWith("kowloon")) {
          mappedRegion = "kowloon";
        } else if (regionVal.includes("hong_kong")) {
          mappedRegion = "hong_kong_island";
        } else {
          // Last resort: check district or region text
          const r = (c.region || "").toString();
          if (/九龍|Kowloon/i.test(r)) {
            mappedRegion = "kowloon";
          } else {
            mappedRegion = "new_territories"; // default
          }
        }
      }

      groups[mappedRegion].push(c);
    });

    return groups;
  }, [centers, areaToRegion]);

  const computeDistrictGroups = useCallback(() => {
    // Group by districtValue, which should match hkLocations area values
    const groups = {};
    centers.forEach((c) => {
      const dk = (c.districtValue || c.district || "Other").toString().trim();
      if (!groups[dk]) groups[dk] = [];
      groups[dk].push(c);
    });
    return groups;
  }, [centers]);

  const computeGroupByMapMarkerID = useCallback(
    (subset) => {
      const groups = {};
      (subset || centers).forEach((c) => {
        const mk =
          c.MapMarkerGroupID ||
          c.mapMarkerGroupID ||
          `${c.latitude}-${c.longitude}`;
        if (!groups[mk]) groups[mk] = [];
        groups[mk].push(c);
      });
      return groups;
    },
    [centers]
  );

  // (individual markers removed in 3-level design)

  // create MapMarkerGroupID cluster markers
  const createGroupMarkers = useCallback(
    (subset) => {
      if (
        !map ||
        !window.google ||
        !window.google.maps ||
        !window.google.maps.Marker ||
        !window.google.maps.SymbolPath
      )
        return;
      clearMarkers(groupMarkers);
      const groups = computeGroupByMapMarkerID(subset);
      const newMarkers = [];
      Object.keys(groups).forEach((mk) => {
        const arr = groups[mk];
        // compute average lat/lng
        let latSum = 0,
          lngSum = 0,
          n = 0;
        arr.forEach((c) => {
          const lat = Number(c.latitude ?? c.lat);
          const lng = Number(c.longitude ?? c.lng);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            latSum += lat;
            lngSum += lng;
            n += 1;
          }
        });
        if (n === 0) return;
        const avgLat = latSum / n;
        const avgLng = lngSum / n;
        try {
          const countLabel = String(arr.length);

          const m = new window.google.maps.Marker({
            position: { lat: avgLat, lng: avgLng },
            map,
            title: `${arr.length} center${arr.length > 1 ? "s" : ""} nearby`,
            label: {
              text: countLabel,
              color: "#111827",
              fontWeight: "600",
              fontSize: "11px",
            },
          });
          m.addListener("click", () => {
            setSelectedGroup(mk);
            setVisibleCenters(arr);
            try {
              map.setCenter({ lat: avgLat, lng: avgLng });
              // remain in clusters level
              if (map.getZoom() < 16) map.setZoom(16);
            } catch (e) {}
            // show an info window listing up to 8 center titles
            try {
              // Build scrollable list with highlighted title, address, and an Open button
              const items = arr
                .map((x, i) => {
                  const border =
                    i > 0
                      ? "border-top:1px solid #e5e7eb; padding-top:6px;"
                      : "";
                  const safeTitle = (x.title || x.id || "")
                    .toString()
                    .replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;");
                  const rawMeta = `${x.address || ""}`;
                  const safeMeta = rawMeta
                    .toString()
                    .replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;");
                  const idStr = encodeURIComponent(String(x.id ?? ""));
                  const slug = (
                    typeof makeSlug === "function"
                      ? makeSlug(x.title || "")
                      : x.title || ""
                  )
                    .toString()
                    .toLowerCase()
                    .replace(/[^\w-]+/g, "-");
                  const buttonHtml =
                    x && x.id
                      ? `<button style="background:#FFA500;color:white;border:none;border-radius:4px;padding:4px 8px;font-size:12px;cursor:pointer;white-space:nowrap;" onclick="window.__openTutorCenter && window.__openTutorCenter('${idStr}','${slug}')">${
                          t("tutorCenters.view") || "Open"
                        }</button>`
                      : "";
                  const metaHtml = safeMeta
                    ? `<div style="font-size:12px;color:#6b7280;margin-top:2px;">${safeMeta}</div>`
                    : "";
                  return `
                    <li style="margin:2px 0; ${border}">
                      <div style="display:flex; align-items:flex-start; gap:8px;">
                        <div style="flex:1 1 auto; min-width:0;">
                          <div style="font-weight:600; color:#2563eb;">${safeTitle}</div>
                          ${metaHtml}
                        </div>
                        ${buttonHtml}
                      </div>
                    </li>`;
                })
                .join("");
              const contentHtml = `
                <div style="max-width:300px">
                  <div style="font-weight:600;margin-bottom:6px;">${t(
                    "tutorCenters.centersShown"
                  )} ${arr.length} ${
                t("tutorCenters.centers") || "centers"
              }</div>
                  <div style="max-height:250px; overflow-y:auto; padding-right:6px;">
                    <ul style="padding-left:11px; margin:0;">${items}</ul>
                  </div>
                </div>`;
              // Ensure only one info window open at a time
              try {
                if (infoWindowRef.current) infoWindowRef.current.close();
              } catch (e) {}
              infoWindowRef.current = new window.google.maps.InfoWindow({
                content: contentHtml,
              });
              infoWindowRef.current.open({ map, anchor: m });
            } catch (e) {}
          });
          newMarkers.push(m);
        } catch (e) {}
      });
      setGroupMarkers(newMarkers);
    },
    [map, groupMarkers, computeGroupByMapMarkerID, t]
  );

  // create district markers (level 2) - uses hkLocations for coordinates and names
  const createDistrictMarkers = useCallback(() => {
    if (
      !map ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Marker ||
      !window.google.maps.SymbolPath
    )
      return;
    clearMarkers(districtMarkers);
    const groups = computeDistrictGroups();
    const newMarkers = [];
    const lang = i18n.language && i18n.language.startsWith("zh") ? "tw" : "en";

    Object.keys(groups).forEach((dk) => {
      const arr = groups[dk];
      if (arr.length === 0) return;

      // Look up coordinates from hkLocations
      const locationInfo = districtLookup[dk];
      let lat, lng, displayName;

      if (
        locationInfo &&
        Number.isFinite(locationInfo.lat) &&
        Number.isFinite(locationInfo.lng)
      ) {
        // Use precise coordinates from hkLocations
        lat = locationInfo.lat;
        lng = locationInfo.lng;
        displayName =
          locationInfo.display[lang] || locationInfo.display.en || dk;
      } else {
        // Fallback: compute average lat/lng from centers in this district
        let latSum = 0,
          lngSum = 0,
          n = 0;
        arr.forEach((c) => {
          const cLat = Number(c.latitude ?? c.lat);
          const cLng = Number(c.longitude ?? c.lng);
          if (Number.isFinite(cLat) && Number.isFinite(cLng)) {
            latSum += cLat;
            lngSum += cLng;
            n += 1;
          }
        });
        if (n === 0) return; // skip if no valid coords
        lat = latSum / n;
        lng = lngSum / n;
        displayName = dk;
      }

      try {
        const countLabel = String(arr.length);
        const labelText = `${displayName}: ${arr.length}`;
        const iconScale = Math.min(
          8 + countLabel.length * 2 + Math.min(arr.length, 200) * 0.08,
          60
        );
        const m = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          title: `${displayName}: ${arr.length} center${
            arr.length > 1 ? "s" : ""
          }`,
          label: {
            text: labelText,
            color: "#111827",
            fontWeight: "600",
            fontSize: "11px",
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: Math.max(1, iconScale / 10) * 20,
            fillColor: "#f59e0b",
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: "#ffffff",
          },
        });
        m.addListener("click", () => {
          // Reset other filters when drilling into a district
          try {
            if (infoWindowRef.current) infoWindowRef.current.close();
          } catch (e) {}
          setSelectedRegion(null);
          setSelectedGroup(null);
          setSelectedDistrict(dk);
          setVisibleCenters(arr);
          // go to cluster level (>=16)
          try {
            map.setCenter({ lat, lng });
            map.setZoom(16);
          } catch (e) {}
        });
        newMarkers.push(m);
      } catch (e) {}
    });
    setDistrictMarkers(newMarkers);
  }, [
    map,
    districtMarkers,
    computeDistrictGroups,
    districtLookup,
    i18n.language,
  ]);

  // create region markers (level 1) - 3 hardcoded merged regions: 新界, 香港島, 九龍
  const createRegionMarkers = useCallback(() => {
    if (
      !map ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Marker ||
      !window.google.maps.SymbolPath
    )
      return;
    clearMarkers(regionMarkers);
    const groups = computeRegionGroups();

    // 3 hardcoded regions with labels
    const entries = [
      {
        key: "new_territories",
        label:
          i18n.language && i18n.language.startsWith("zh")
            ? "新界"
            : "New Territories",
        arr: groups.new_territories,
      },
      {
        key: "hong_kong_island",
        label:
          i18n.language && i18n.language.startsWith("zh")
            ? "香港島"
            : "Hong Kong Island",
        arr: groups.hong_kong_island,
      },
      {
        key: "kowloon",
        label:
          i18n.language && i18n.language.startsWith("zh") ? "九龍" : "Kowloon",
        arr: groups.kowloon,
      },
    ];

    const newMarkers = [];
    entries.forEach((entry) => {
      const arr = entry.arr || [];
      if (arr.length === 0) return; // skip if no centers

      // compute avg lat/lng for the region from its centers
      let latSum = 0,
        lngSum = 0,
        n = 0;
      arr.forEach((c) => {
        const lat = Number(c.latitude ?? c.lat);
        const lng = Number(c.longitude ?? c.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          latSum += lat;
          lngSum += lng;
          n += 1;
        }
      });
      if (n === 0) return;
      const avgLat = latSum / n;
      const avgLng = lngSum / n;

      try {
        const countLabel = String(arr.length);
        const iconScale = Math.min(
          8 + countLabel.length * 2 + Math.min(arr.length, 200) * 0.08,
          60
        );
        const labelText = `${entry.label}: ${arr.length}`;

        const m = new window.google.maps.Marker({
          position: { lat: avgLat, lng: avgLng },
          map,
          title: `${entry.label}: ${arr.length} center${
            arr.length > 1 ? "s" : ""
          }`,
          label: {
            text: labelText,
            color: "#111827",
            fontWeight: "600",
            fontSize: "12px",
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: Math.max(1, iconScale / 10) * 15,
            fillColor: "#f59e0b",
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: "white",
          },
        });
        m.addListener("click", () => {
          setSelectedRegion(entry.key);
          setVisibleCenters(arr);
          // go to district level (14-15)
          try {
            try {
              if (infoWindowRef.current) infoWindowRef.current.close();
            } catch (e) {}
            map.setCenter({ lat: avgLat, lng: avgLng });
            map.setZoom(14);
          } catch (e) {}
        });
        newMarkers.push(m);
      } catch (e) {}
    });
    setRegionMarkers(newMarkers);
  }, [map, regionMarkers, computeRegionGroups, i18n.language]);

  // main effect: re-create region markers when centers/map ready
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;
    // clear lower levels
    clearMarkers(districtMarkers);
    clearMarkers(groupMarkers);
    clearMarkers(individualMarkers);
    setDistrictMarkers([]);
    setGroupMarkers([]);
    setIndividualMarkers([]);
    createRegionMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, centers]);

  // zoom change handler to orchestrate level transitions
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;
    const onZoom = () => {
      // Always ensure only one info window; close on zoom changes
      try {
        if (infoWindowRef.current) infoWindowRef.current.close();
      } catch (e) {}
      const z = map.getZoom();
      if (z <= 13) {
        // show regions only
        clearMarkers(districtMarkers);
        setDistrictMarkers([]);
        clearMarkers(groupMarkers);
        setGroupMarkers([]);
        clearMarkers(individualMarkers);
        setIndividualMarkers([]);
        if (!regionMarkers || regionMarkers.length === 0) {
          // Ensure region markers exist at this level
          createRegionMarkers();
        } else {
          regionMarkers.forEach((m) => {
            try {
              m.setMap(map);
            } catch (e) {}
          });
        }
        // reset all filters on zoom back to regions
        setSelectedRegion(null);
        setSelectedDistrict(null);
        setSelectedGroup(null);
        setVisibleCenters([]);
      } else if (z >= 14 && z <= 15) {
        // show district markers (hide others)
        clearMarkers(regionMarkers);
        clearMarkers(groupMarkers);
        setGroupMarkers([]);
        clearMarkers(individualMarkers);
        setIndividualMarkers([]);
        if (!districtMarkers || districtMarkers.length === 0) {
          // Ensure district markers are created for this level
          createDistrictMarkers();
        } else {
          districtMarkers.forEach((m) => {
            try {
              m.setMap(map);
            } catch (e) {}
          });
        }
        // reset filters when zoomed back to district level
        setSelectedRegion(null);
        setSelectedDistrict(null);
        setSelectedGroup(null);
        setVisibleCenters([]);
      } else if (z >= 16) {
        // show MapMarkerGroupID clusters for current selection
        clearMarkers(regionMarkers);
        clearMarkers(districtMarkers);
        clearMarkers(individualMarkers);
        setIndividualMarkers([]);
        // if user selected a district, create clusters for that district
        if (selectedDistrict) {
          const districtCenters = centers.filter(
            (c) =>
              ((c.districtValue || c.district || "") + "").toString() ===
              selectedDistrict
          );
          createGroupMarkers(districtCenters);
        } else if (selectedRegion) {
          // create clusters for the region's centers
          const regionCenters = centers.filter((c) => {
            const rv = (c.regionValue || c.region || "") + "";
            if (selectedRegion === "new_territories")
              return rv.startsWith("new_territories");
            if (selectedRegion === "kowloon") return rv.startsWith("kowloon");
            if (selectedRegion === "hong_kong_island")
              return rv.includes("hong_kong");
            return false;
          });
          createGroupMarkers(regionCenters);
        } else {
          // fallback: create groups for all centers
          createGroupMarkers(centers);
        }
      }
    };
    const listener = map.addListener("zoom_changed", onZoom);
    return () => {
      try {
        if (listener) window.google.maps.event.removeListener(listener);
      } catch (e) {}
    };
  }, [
    map,
    regionMarkers,
    districtMarkers,
    groupMarkers,
    individualMarkers,
    selectedRegion,
    selectedDistrict,
    visibleCenters,
    centers,
    createGroupMarkers,
    createRegionMarkers,
    createDistrictMarkers,
  ]);

  // expose a window helper to open center in new tab (keeps parity with previous code)
  useEffect(() => {
    window.__selectTutorCenter = (index) => {
      const c = centers && centers[index];
      if (!c) return;
      try {
        if (typeof onSelectCenter === "function")
          onSelectCenter(c, {
            selectedRegion,
            selectedDistrict,
            selectedGroup,
          });
      } catch (e) {}
      try {
        const slug = makeSlug(c.title) || c.id || "";
        if (c && c.id) window.open(`/tutorCenters/${c.id}/${slug}`, "_blank");
      } catch (e) {}
    };
    return () => {
      try {
        delete window.__selectTutorCenter;
      } catch (e) {}
    };
  }, [
    centers,
    onSelectCenter,
    selectedRegion,
    selectedDistrict,
    selectedGroup,
  ]);

  // expose a simple global opener for InfoWindow buttons
  useEffect(() => {
    window.__openTutorCenter = (id, slug) => {
      if (!id) return;
      try {
        const s = (slug && String(slug).trim()) || "";
        window.open(`/tutorCenters/${id}/${s}`, "_blank");
      } catch (e) {
        try {
          window.open(`/tutorCenters/${id}`, "_blank");
        } catch (e2) {}
      }
    };
    return () => {
      try {
        delete window.__openTutorCenter;
      } catch (e) {}
    };
  }, []);

  if (isLoading) {
    return (
      <div className="w-full h-[600px] flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t("tutorCenters.loadingMap")}</p>
        </div>
      </div>
    );
  }

  const selectCenter = (c) => {
    if (!c) return;
    try {
      if (typeof onSelectCenter === "function")
        onSelectCenter(c, {
          selectedRegion,
          selectedDistrict,
          selectedGroup,
        });
      const slug = makeSlug(c.title) || c.id || "";
      if (c && c.id) window.open(`/tutorCenters/${c.id}/${slug}`, "_blank");
    } catch (e) {}
  };

  return (
    <div className="w-full md:flex md:flex-row md:gap-4">
      <div className="flex-1">
        <div className="relative">
          <div
            ref={mapRef}
            className="w-full h-[600px] rounded-lg shadow-md"
            style={{ minHeight: "600px" }}
          />
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p>
            {t("tutorCenters.mapNote") ||
              "Map shows locations based on addresses."}
          </p>
          <p className="mt-1">
            {t("tutorCenters.centersShown") || "Showing"} {centers.length}
          </p>
        </div>
      </div>

      <aside className="md:w-80 lg:w-1/4 mt-4 md:mt-0">
        <div className="h-[600px] overflow-auto rounded-lg border border-gray-100 bg-white shadow-sm">
          {visibleCenters.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">
              {t("tutorCenters.selectAreaPrompt") ||
                "Select an area to see centers."}
            </div>
          ) : (
            <>
              <div className="px-4 py-2 text-xs uppercase tracking-wide text-gray-500 bg-gray-50 sticky top-0">
                {selectedGroup
                  ? t("tutorCenters.centersInCluster") || "Centers in cluster"
                  : selectedDistrict
                  ? t("tutorCenters.centersInDistrict") || "Centers in district"
                  : selectedRegion
                  ? t("tutorCenters.centersInRegion") || "Centers in region"
                  : t("tutorCenters.selectedCenters") || "Selected centers"}
              </div>
              <ul className="divide-y">
                {visibleCenters.map((c, i) => (
                  <li
                    key={`${c.id || i}-${i}`}
                    className="p-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <button
                      onClick={() => selectCenter(c)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-800">
                            {c.title || c.id}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {c.district || c.region || ""}
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-[220px]">
                            {c.address || ""}
                          </div>
                        </div>
                        <div className="ml-2 text-xs text-blue-500 hover:text-blue-700">
                          {t("tutorCenters.view") || "View"}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default TutorCenterMapPanel;
